import { observer } from "mobx-react";
import FontBody1 from "../../../../../../components/fonts/font-body1/font-body1";
import FontBody2 from "../../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../../components/fonts/font-props";
import { FormFieldLine } from "../../../../../../components/form-field-line/form-field-line";
import { Input } from "../../../../../../components/inputs/input/input";
import { IOption, SelectDropdown } from "../../../../../../components/inputs/select-dropdown/select-dropdown";
import { Toggle } from "../../../../../../components/inputs/toggle/toggle";
import Card from "../../../../../../components/layouts/card/card";
import {
	ALLOW_OUT_OF_STOCK_EXCHANGE_OPTION,
	FALLBACK_SHIPPING_TYPE,
	REFUND_BY,
	RESOLUTION_ID,
	SHIPPING_LINE_TYPE,
	SHIPPING_LINE_TYPES_WITH_TITLE_MAP
} from '../../../../constants';
import resolutionListStore from "../../../../stores/resolution-list-store";
import ValueRange from "../../components/value-range/value-range";
import { IResolutionTypeProps } from "../resolution-type-viewer-props";
import currentPortalStore from "../../../../../../stores/current-portal-store";
import { FUNC } from "../../../../../../stores/platform-service-store";
import { IExchangeRefundProviderOption } from "../../../../../../services/api/resolutions-service";
import { FEATURES } from "../../../../../../models/Plan";
import { FALLBACK_EXCHANGE_ORDER_SHIPPING_OPTIONS } from "./constants";
import ResolveCapability from "../../../../../../components/resolve-capability/resolve-capability";
import { PROFILE_CAPABILITIES } from "../../../../../../models/ProfileCapabilities";
import { ShippingTitleFieldLine } from "./shippin-title-field-line";

const Exchange: React.FC<IResolutionTypeProps> = observer(({ resolution, validationGroup }) => {
    const platform = currentPortalStore.platform;
    const varinatPriceRanges = resolution?.getOfferRanges();
    const refundByOptions = resolutionListStore.getRefundByOptions()
        .filter(option => platform?.isRefundMethodSupportedByValue(option.value as REFUND_BY));
    const refundBySelected = refundByOptions.find(option => option.value === resolution?.selectedRefundBy);
    const allowOutOfStockExchangeOptions = resolutionListStore.allowOutOfStockExchangeOptions
      .filter(option => {
          return option.value !== ALLOW_OUT_OF_STOCK_EXCHANGE_OPTION.SOME || platform?.isFunctionalitySupported(FUNC.FUNC_ALLOW_PARTIAL_OUT_OF_STOCK);
      });
    const allowOutOfStockExchangeSelected = allowOutOfStockExchangeOptions.find(option => {
        return option.value === resolution?.allowOutOfStockExchanging;
    });
    const isProductExchange = resolution?.resolutionId === RESOLUTION_ID.PRODUCT_EXCHANGE;
    const allowOutOfStockCapability = isProductExchange
        ? PROFILE_CAPABILITIES.PRODUCT_EXCHANGE
        : PROFILE_CAPABILITIES.VARIANT_EXCHANGE;
    const isAnyToAny = isProductExchange && currentPortalStore.isFeatureAllowed(FEATURES.ANY_TO_ANY_EXCHANGES_FEATURE);
    const isPriceDifferenceToggleShown = (forcedValue: boolean | null, isProductExchangeExempt: boolean): boolean =>
        forcedValue == null || (isProductExchange && isProductExchangeExempt);
    const shippingLineOptions = resolution?.shippingSettingsStore?.getFilteredShippingLineOptions(platform);
    const shouldShowShippingTitleFieldLine = resolution && SHIPPING_LINE_TYPES_WITH_TITLE_MAP.includes(resolution?.shippingSettingsStore?.shippingLine!);
    const shouldShowFallbackBehaviorFieldLine = resolution?.shippingSettingsStore.shippingLine === SHIPPING_LINE_TYPE.SELECT_SPECIFIC_FREE_SHIPPING;

    const refundByComponent = (
        <FormFieldLine
            label="Refund by"
            tooltip={
                <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                    How the price difference is returned when a customer chooses a lower-priced variant.
                </FontBody2>
            }
        >
            <SelectDropdown
                value={refundBySelected}
                options={refundByOptions}
                onChange={(v) => {
                    if (v) {
                        resolution?.setRefundBy(v as IExchangeRefundProviderOption)
                    }
                }}
                required
                validationGroup={validationGroup}
            />
        </FormFieldLine>
    );

    return (
        <>
            { !isAnyToAny && (
                <ResolveCapability capability={PROFILE_CAPABILITIES.EXCHANGE_RESOLUTION}>
                    {({
                        presentMoreExpensiveForcedValue,
                        indicateLessExpensiveForcedValue,
                        isProductExchangeExemptFromForcedPriceDifference,
                    }) => (
                        <Card
                            toolbar={{
                                title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>Pricing</FontBody1>,
                            }}
                        >
                            <FormFieldLine
                                label="No extra charge or refund within this price range"
                                note="Price range (% of the original item) where the exchange is even. 100%–100% = same price only."
                            >
                                <ValueRange
                                    from={{
                                        value: resolution?.rangeFrom,
                                        default: varinatPriceRanges?.[0] ?? 0,
                                        callback: (v) => resolution?.setRangeFrom(v),
                                    }}
                                    to={{
                                        value: resolution?.rangeTo,
                                        default: varinatPriceRanges?.[1] ?? 0,
                                        callback: (v) => resolution?.setRangeTo(v),
                                    }}
                                    disabled={isAnyToAny}
                                />
                            </FormFieldLine>

                            {isPriceDifferenceToggleShown(presentMoreExpensiveForcedValue, isProductExchangeExemptFromForcedPriceDifference) && (
                                <FormFieldLine
                                    label="Above the price range — allow exchanges to more expensive variants"
                                    note={<>
                                        <span>On: Customers can choose a more expensive option and pay the difference.</span>
                                        <br/>
                                        <span>Off: More expensive options can't be selected.</span>
                                    </>}
                                >
                                    <Toggle
                                        checked={!!resolution?.presentMoreExpensive}
                                        label=""
                                        onChange={(v) => resolution?.setPresentMoreExpensive(v)}
                                        disabled={isAnyToAny}
                                    />
                                </FormFieldLine>
                            )}

                            {isPriceDifferenceToggleShown(indicateLessExpensiveForcedValue, isProductExchangeExemptFromForcedPriceDifference) && (
                                <FormFieldLine
                                    label="Below the price range — allow exchanges to cheaper variants"
                                    note={<>
                                        <span>On: Customers can choose a cheaper option and get the difference back.</span>
                                        <br/>
                                        <span>Off: Cheaper options can't be selected.</span>
                                    </>}
                                >
                                    <Toggle
                                        checked={!!resolution?.indicateLessExpensive}
                                        label=""
                                        onChange={(v) => resolution?.setIndicateLessExpensive(v)}
                                        disabled={isAnyToAny}
                                    />
                                </FormFieldLine>
                            )}

                            {!!resolution?.indicateLessExpensive && refundByComponent}
                        </Card>
                    )}
                </ResolveCapability>
            )}

            <Card
                toolbar={{
                    title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>Stock</FontBody1>,
                }}
            >
                <ResolveCapability capability={allowOutOfStockCapability}>
                    {({ isAllowOutOfStockSupported }) => isAllowOutOfStockSupported && (
                        <FormFieldLine
                            label="Allow out-of-stock exchange"
                            note="Set whether customers can select out-of-stock variants."
                        >
                            <SelectDropdown
                              value={allowOutOfStockExchangeSelected}
                              options={allowOutOfStockExchangeOptions}
                              onChange={(v) => {
                                  if (v) {
                                      resolution?.setAllowOutOfStockExchanging(v.value as ALLOW_OUT_OF_STOCK_EXCHANGE_OPTION)
                                  }
                              }}
                              required
                              validationGroup={validationGroup}
                            />
                        </FormFieldLine>
                    )}
                </ResolveCapability>

                <FormFieldLine
                    label="Minimum available stock threshold"
                    note={'A variant will not be available for exchange when the remaining stock is smaller than or equal to the number you define.'}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px'}}>
                        <Input
                            value={resolution?.minimumAvailableStockThreshold}
                            onChange={(v) => resolution?.setMinimumAvailableStockThreshold(v as string)}
                            maxlength={6}
                        />
                        <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>in stock</FontBody2>
                    </div>
                </FormFieldLine>

                {!!isAnyToAny && refundByComponent}

                <FormFieldLine
                    label="Size Chart URL"
                    note={'URL to display customers for "Size" variant exchanges.'}
                >
                    <Input
                        value={resolution?.sizeChartUrl}
                        onChange={(v) => resolution?.setSizeChartUrl(v as string)}
                        placeholder="e.g.: https://www.your.store.com/sizechart/"
                    />
                </FormFieldLine>

	            <ResolveCapability capability={PROFILE_CAPABILITIES.VARIANT_EXCHANGE}>
		            {({ isSizeOptionSettingSupported }) => isSizeOptionSettingSupported && (
			            <FormFieldLine
				            label="Size variant option name (optional)"
				            note="The size chart link will appear only when the product includes the variant option name set in this field"
				            tooltip={
					            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
						            When empty, the size chart link will appear for products
						            <br />
						            with a variant option named "Size" in English
					            </FontBody2>
				            }
			            >
				            <Input
					            value={resolution?.sizeOptionName}
					            onChange={(v) => resolution?.setSizeOptionName(v as string)}
					            placeholder={`e.g "Size"`}
				            />
			            </FormFieldLine>
		            )}
	            </ResolveCapability>
            </Card>

	        <ResolveCapability capability={PROFILE_CAPABILITIES.EXCHANGE_RESOLUTION}>
		        {({ isExchangeOrderShippingSettingSupported }) => isExchangeOrderShippingSettingSupported && (
			        <Card
				        toolbar={{
					        title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>Shipping</FontBody1>,
				        }}
			        >
				        <FormFieldLine
					        label="Exchange order shipping"
					        note='Configure how shipping is handled when creating new exchange orders. Shipping will be either free or require manual configuration'
				        >
					        <SelectDropdown
						        value={shippingLineOptions?.find((o) => o.value === resolution?.shippingSettingsStore.shippingLine)}
						        // Explanation: TypeScript cannot guarantee that the resolution store is defined.
						        // However, at runtime this component only renders when resolution is available.
						        // Therefore, we explicitly tell TypeScript to treat it as always defined.
						        options={shippingLineOptions as IOption[]}
						        onChange={(v) => resolution?.shippingSettingsStore?.setShippingLine(v?.value as SHIPPING_LINE_TYPE) }
						        required
						        validationGroup={validationGroup}
					        />
				        </FormFieldLine>

                        {shouldShowShippingTitleFieldLine && (
                            <ShippingTitleFieldLine resolution={resolution} validationGroup={validationGroup} />
                        )}

				        {shouldShowFallbackBehaviorFieldLine && (
					        <FormFieldLine
						        label="Fallback Behavior"
						        note='If the selected shipping method is not available:'
					        >
						        <SelectDropdown
							        value={FALLBACK_EXCHANGE_ORDER_SHIPPING_OPTIONS.find((o) => o.value === resolution?.shippingSettingsStore.fallbackShippingLine)}
							        options={FALLBACK_EXCHANGE_ORDER_SHIPPING_OPTIONS}
							        onChange={(v) => resolution?.shippingSettingsStore?.setFallbackShippingLine(v?.value as FALLBACK_SHIPPING_TYPE)}
							        required
							        validationGroup={validationGroup}
						        />
					        </FormFieldLine>
				        )}
			        </Card>
		        )}
	        </ResolveCapability>
        </>
    )
});

export default Exchange;
